/*
 *  router.js
 *
 * This file is the main entry point to the app.
 *
 * The app is build on react and reflux and uses firebase as a database and auth backend.
 *
 */
// Leave this here, since its initalites the firebase app
import { initApp } from "./utils/firebaseUtils";

initApp();

// Import the theme - needs to be required to make it work

import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import moment from "moment";
import React, { Suspense } from "react";
import ReactDOM from "react-dom";
import Reflux from "reflux";

import {
  Account__Login,
  CompanySetup,
  CustomerSucessController,
  Integration__Bite,
  Integration__DVinci,
  Integration__HRPuls,
  Integration__Onlyfy,
  Integration__Personio,
  Integration__Recruitee,
  Integration__Rexx,
  Integration__MHM,
  InviteMember,
  InviteMemberData,
  InviteTeam,
  Login,
  ResetPassword,
  Settings__ATS,
  Settings__Business,
  SettingsCampaigns,
  Settings__ChangeEmail,
  Settings__Integration,
  Settings__Invoice,
  Settings__Profile,
  BrandKit,
  Settings__TeamMember,
  // Talenthub
  SidebarProvider,
  Talenthub__Templates,
  Talenthub_ApplicationOnboarding,
  Talenthub_Questions,
  Talenthub__Automations,
  Talenthub__Privacy,
  Signup,
  SignupOrganization,
  Thanks,
  VerificationCode
  // eslint-disable-next-line import/no-unresolved
} from "@talentbait/shared-components";

import "@draft-js-plugins/hashtag/lib/plugin.css";
import "../css/global.css";

import { Redirect, Route, BrowserRouter as Router, Switch } from "react-router-dom";
import logger from "./utils/LoggerFrontend";

// This enables DataDog Real-Time user monitoring
import "./utils/DataDogRUM";

// Actions
import Actions from "./Actions";
import CampaignCreateStore from "./Stores/CampaignCreationStore";
import StatusStore from "./Stores/StatusStore";
import auth from "./utils/AuthController";

// Views
import LoadingView from "./Components/LoadingView";
import Jobs from "./PagesUser/jobs/Jobs";
import Billing from "./PagesUser/billing/Billing";
import PrepaidBilling from "./PagesUser/PrepaidBilling";
import Profile from "./PagesUser/profiles/profile/Profile";

// other
import useLDContext from "./hooks/useLDContext";

// Default layout
import EndUserLayout from "./layouts/enduser-layout/EndUserLayout";
import PrintUserLayout from "./layouts/printuser-layout/PrintUserLayout";

// Static Pages
import AdPreviewToolSelfService from "./PagesAdmin/AdPreviewToolSelfService/AdPreviewTool_SelfService";
import CampaignRead from "./PagesUser/Campaign/Campaign__Read";
import DraftCampaign from "./PagesUser/DraftCampaign";
import Privacy from "./PagesUser/Privacy";
import SelfServiceCampaignCreate from "./PagesUser/SelfServiceCampaignCreation/campaign-creation/CampaignCreate";
import EmployerBrandingSelector from "./PagesUser/SelfServiceCampaignCreation/EmployerBranding/EmployerBranding";

import AdsetRead from "./PagesUser/Campaign/Adset__Read";
import i18n from "./i18n";
import datadogRum from "./utils/DataDogRUM";

import MetricsReport from "./Components/MetricsReport/MetricsReport";
import ButtonsOverview from "./PagesUser/ButtonsOverview";
import ChooseCampaign from "./PagesUser/SelfServiceCampaignCreation/ChooseCampaign/ChooseCampaign";
import MediaCenter from "./PagesUser/MediaCenter";
import DataVisualization from "./PagesUser/Campaign/DataVisualization/DataVisualization";
import NewMetricsReport from "./Components/NewMetricsReport/NewMetricsReport";
import AtsThankYou from "./LeadFormViews/AtsThankYou";
import AdAssistant from "./PagesUser/ad-assistant/AdAssistant";
import Chatbot from "./PagesUser/ad-assistant/chat-bot/Chatbot";
import PublicCreativesView from "./PagesAdmin/AdPreviewToolSelfService/PublicCreativesView";
import { ChatbotContextProvider } from "./PagesUser/ad-assistant/chat-bot/Chatbot.context";
import AssistantLayout from "./PagesUser/ad-assistant/assistant-layout/AssistantLayout";
import AdAssistantOverview from "./PagesUser/ad-assistant/overview/AdAssistantOverview";
import AdAssistantBudgetCheck from "./PagesUser/ad-assistant/budget-check/AdAssistantBudgetCheck";
import AdAssistantCheckout from "./PagesUser/ad-assistant/checkout/AdAssistantCheckout";
import AdAssistantThankYou from "./PagesUser/ad-assistant/thank-you/AdAssistantThankYou";
import CampaignsOverview from "./PagesUser/campaigns-overview/CampaignsOverview";
import AllCampaignsDashboard from "./PagesUser/ad-assistant/dashboard/AllCampaignsDashboard";
import loadGoogleMaps from "./utils/LoadGoogleMapsScript";
import ProfileDashboard from "./PagesUser/profiles/profile-dashboard/ProfileDashboard";
import GlobalMediaLibrary from "./PagesUser/global-media-library/GlobalMediaLibrary";

const mountNode = document.getElementById("app");
const VALIDATION_START_DATE = "2022-07-23T18:00:17+02:00";
// NOTE: The config object is defined in index.ejs and is passed in from the backend
// Enable experimentalForceLongPolling to bypass strong corporate Firewalls as it is recommended by firebase
// https://github.com/firebase/firebase-js-sdk/issues/1674 for such use cases

Reflux.initStore(StatusStore);
Reflux.initStore(CampaignCreateStore);

let loc = "";

function RouteWithLayout({ layout, component, ...rest }) {
  const flags = useLDContext();
  const cleanUrl = window.location.origin + window.location.pathname;

  if (cleanUrl !== loc) {
    loc = cleanUrl;
    CustomerSucessController.page();
  }

  return (
    <Route
      {...rest}
      render={(props) =>
        React.createElement(
          layout,
          { ...props, flags },
          React.createElement(component, { ...props, flags })
        )
      }
    />
  );
}

function RouteWithLayoutAndRender({ layout, ...rest }) {
  const flags = useLDContext();
  const cleanUrl = window.location.origin + window.location.pathname;

  if (cleanUrl !== loc) {
    loc = cleanUrl;
    CustomerSucessController.page();
  }

  return (
    <Route
      {...rest}
      render={(props) => React.createElement(layout, props, rest.render({ ...props, flags }))}
    />
  );
}

const EmptyProvider = ({ children }) => children;

async function getLDProvider(userProfile) {
  const { clientConfig, user } = window;

  if (!clientConfig?.LAUNCH_DARKLY_CLIENT_ID || !user) {
    return EmptyProvider;
  }

  const userContext = {
    user: {
      key: user?.uid,
      name: user?.displayName,
      email: user?.email,
      isUsingNewRole: user.isUsingNewRole || false,
      rolesMigrationGroup: user.rolesMigrationGroup || "default"
    }
  };

  const orgaContext = userProfile?.organizationId
    ? {
        organization: {
          key: userProfile.organizationId,
          isUsingNewRole: userProfile.isUsingNewRole || false,
          rolesMigrationGroup: userProfile.rolesMigrationGroup || "default"
        }
      }
    : null;

  try {
    return await asyncWithLDProvider({
      clientSideID: clientConfig.LAUNCH_DARKLY_CLIENT_ID,
      context: {
        kind: "multi",
        ...userContext,
        ...orgaContext
      }
    });
  } catch (error) {
    logger.log("error:router:getLDProvider", "asyncWithLDProvider error", error);
    return EmptyProvider;
  }
}

let mounted = false;

async function renderDOM(userProfile) {
  // we have multiple logical entry triggers for the app like userProfileLoaded or userLoggedIn
  // we originally only called one of these but now we call multiple, possibly multiple times
  // this caused to app to be force inited (mounted) multiple times
  // the below line mitigates this
  if (mounted) {
    return;
  }

  Actions.setPosthogConfig();
  const LDProvider = await getLDProvider(userProfile);

  ReactDOM.render(
    <Suspense fallback={null}>
      <LDProvider>
        <SidebarProvider>
          <Router>
            <Switch>
              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/signup"
                render={(props) => <Signup {...props} plgCustomer />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/signup-organization"
                render={(props) => <SignupOrganization {...props} plgCustomer />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/verification-code"
                render={(props) => <VerificationCode {...props} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/setup"
                render={(props) => <CompanySetup {...props} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/invite-team"
                render={(props) => <InviteTeam {...props} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/thanks"
                render={(props) => <Thanks {...props} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/registrieren"
                render={() => <Redirect to="/signup" />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/invite/:orgaId/:memberId"
                render={(props) => <InviteMember {...props} currentUser={auth.getCurrentUser()} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/invite/:orgaId/:memberId/data"
                render={(props) => (
                  <InviteMemberData {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/onboarding/:step?"
                render={(props) => <DraftCampaign {...props} currentUser={auth.getCurrentUser()} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/password-reset"
                render={(props) => (
                  <ResetPassword
                    {...props}
                    sendPasswordResetToEmail={auth.sendPasswordResetToEmail}
                  />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/login"
                render={(props) => <Login {...props} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/login/:orgaId/:memberId"
                render={(props) => <Account__Login {...props} />}
              />

              {/* Media Library */}

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/media-library"
                render={(props) => <GlobalMediaLibrary {...props} />}
              />

              {/* Ad Assistant */}

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/assistant-dashboard"
                render={(props) => <AllCampaignsDashboard {...props} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/assistant"
                render={(props) => (
                  <ChatbotContextProvider>
                    <Chatbot {...props} />
                  </ChatbotContextProvider>
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/assistant/:campaignId"
                render={(props) => (
                  <ChatbotContextProvider>
                    <Chatbot {...props} isOnboardingCampaign />
                  </ChatbotContextProvider>
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/budget-check-assistant"
                render={(props) => (
                  <ChatbotContextProvider>
                    <Chatbot {...props} isBudgetCheck />
                  </ChatbotContextProvider>
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/assistant/preview/:campaignId"
                render={(props) => (
                  <AssistantLayout>
                    <AdAssistant {...props} />
                  </AssistantLayout>
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/assistant/overview/:campaignId"
                render={(props) => (
                  <AssistantLayout>
                    <AdAssistantOverview {...props} />
                  </AssistantLayout>
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/assistant/budget-check/:campaignId"
                render={(props) => (
                  <AssistantLayout>
                    <AdAssistantBudgetCheck {...props} />
                  </AssistantLayout>
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/assistant/checkout/:campaignId"
                render={(props) => (
                  <AssistantLayout>
                    <AdAssistantCheckout {...props} />
                  </AssistantLayout>
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/assistant/thank-you/:campaignId"
                render={(props) => (
                  <AssistantLayout>
                    <AdAssistantThankYou {...props} />
                  </AssistantLayout>
                )}
              />
              {/******/}

              {/* shared settings */}
              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/profile"
                render={(props) => (
                  <Settings__Profile {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/brand-kit"
                render={(props) => <BrandKit {...props} currentUser={auth.getCurrentUser()} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/business"
                render={(props) => (
                  <Settings__Business {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/campaigns"
                render={(props) => (
                  <SettingsCampaigns {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/invoice"
                render={(props) => (
                  <Settings__Invoice {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/ats"
                render={(props) => <Settings__ATS {...props} currentUser={auth.getCurrentUser()} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/integration"
                render={(props) => (
                  <Settings__Integration {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/integration/onlyfy"
                render={(props) => (
                  <Integration__Onlyfy {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/integration/personio"
                render={(props) => (
                  <Integration__Personio {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/integration/recruitee"
                render={(props) => (
                  <Integration__Recruitee {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/integration/bite"
                render={(props) => (
                  <Integration__Bite {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/integration/hr-puls"
                render={(props) => (
                  <Integration__HRPuls {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/integration/rexx"
                render={(props) => (
                  <Integration__Rexx {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/integration/mhm"
                render={(props) => (
                  <Integration__MHM {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/integration/dvinci"
                render={(props) => (
                  <Integration__DVinci {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/settings/team-members"
                render={(props) => (
                  <Settings__TeamMember {...props} currentUser={auth.getCurrentUser()} clientView />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/change-email"
                render={(props) => (
                  <Settings__ChangeEmail
                    {...props}
                    currentUser={auth.getCurrentUser()}
                    getCredentials={auth.getCredentials}
                  />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/configuration/applicant"
                render={(props) => (
                  <Talenthub_ApplicationOnboarding {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/configuration/questions"
                render={(props) => (
                  <Talenthub_Questions {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/configuration/templates"
                render={(props) => (
                  <Talenthub__Templates {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/configuration/privacy"
                render={(props) => (
                  <Talenthub__Privacy {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                exact
                path="/configuration/automations"
                render={(props) => (
                  <Talenthub__Automations {...props} currentUser={auth.getCurrentUser()} />
                )}
              />

              {/* A special endpoint for redirecting members */}
              <RouteWithLayout layout={EndUserLayout} path="/run" component={LoadingView} />
              <RouteWithLayout
                layout={EndUserLayout}
                path="/dashboard"
                component={CampaignsOverview}
              />

              <RouteWithLayout layout={EndUserLayout} path="/jobs" component={Jobs} />

              <RouteWithLayout
                layout={EndUserLayout}
                path="/profiles"
                component={ProfileDashboard}
                exact
              />
              <RouteWithLayout layout={EndUserLayout} path="/profiles/:id" component={Profile} />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/budget-check"
                render={(props) => <SelfServiceCampaignCreate {...props} isBudgetCheck />}
              />

              <RouteWithLayout
                layout={EndUserLayout}
                path="/new-campaign/:draftCampaignId"
                component={SelfServiceCampaignCreate}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/new-campaign"
                render={(props) => <SelfServiceCampaignCreate {...props} clientView />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/employer-branding"
                render={(props) => <EmployerBrandingSelector {...props} clientView />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/choose-campaign"
                render={(props) => <ChooseCampaign {...props} clientView />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/media-center"
                render={(props) => <MediaCenter {...props} clientView />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/creatives/:campaignId/:adsetId"
                render={(props) => <AdPreviewToolSelfService {...props} clientView />}
              />
              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/creatives/:campaignId"
                render={(props) => <AdPreviewToolSelfService {...props} clientView />}
              />
              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/preview-creatives/:campaignId/:adsetId/shared"
                render={(props) => <PublicCreativesView {...props} clientView previewOnly />}
              />
              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/preview-creatives/:campaignId"
                render={(props) => <AdPreviewToolSelfService {...props} clientView previewOnly />}
              />

              <RouteWithLayout
                layout={EndUserLayout}
                path="/adset/:campaignId/:adsetId"
                component={AdsetRead}
              />

              <RouteWithLayout
                layout={PrintUserLayout}
                path="/campaign/report/:orgaId/:id"
                component={MetricsReport}
              />

              <RouteWithLayout
                layout={PrintUserLayout}
                exact
                path="/campaign/metrics-report/:orgaId/:id"
                component={NewMetricsReport}
              />

              <RouteWithLayout
                layout={PrintUserLayout}
                path="/campaign/metrics-report/interactions/:orgaId/:id"
                render={(props) => <NewMetricsReport {...props} />}
              />

              <RouteWithLayoutAndRender
                layout={PrintUserLayout}
                exact
                path="/campaign/metrics-report/interaction/:orgaId/:id"
                render={(props) => <NewMetricsReport {...props} interaction />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/campaign/:orgaId/:id"
                render={(props) => <CampaignRead {...props} />}
              />

              <RouteWithLayoutAndRender
                layout={EndUserLayout}
                path="/campaign-data/:orgaId/:id"
                render={(props) => <DataVisualization {...props} />}
              />

              <RouteWithLayout layout={EndUserLayout} path="/buttons" component={ButtonsOverview} />

              <RouteWithLayout layout={EndUserLayout} path="/billing/:orgaId" component={Billing} />
              <RouteWithLayout
                layout={EndUserLayout}
                path="/prepaid-billing/:orgaId"
                component={PrepaidBilling}
              />

              <RouteWithLayout layout={EndUserLayout} path="/privacy" component={Privacy} />

              <Route
                path="/thank-you/:orgaId/:campaignId/:jobCategory"
                exact
                component={AtsThankYou}
              />

              <RouteWithLayout layout={EndUserLayout} path="/" component={CampaignsOverview} />
            </Switch>
          </Router>
        </SidebarProvider>
      </LDProvider>
    </Suspense>,
    mountNode
  );
  mounted = true;
}

//------------------------------------------------------
// Main entry point to start the app by logging in the user via firebase/auth
//------------------------------------------------------
Actions.userLoggedIn();
let userIdLoggedIn = false;
let userLoadProfileRetryCounter = 0;

function addVisibilityHandler() {
  // Set the name of the hidden property and the change event for visibility
  let hidden, visibilityChange;
  if (typeof document.hidden !== "undefined") {
    // Opera 12.10 and Firefox 18 and later support
    hidden = "hidden";
    visibilityChange = "visibilitychange";
  } else if (typeof document.msHidden !== "undefined") {
    hidden = "msHidden";
    visibilityChange = "msvisibilitychange";
  } else if (typeof document.webkitHidden !== "undefined") {
    hidden = "webkitHidden";
    visibilityChange = "webkitvisibilitychange";
  }

  // If the page is hidden, pause the video;
  // if the page is shown, play the video
  function handleVisibilityChange(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    let loc = window.location.href;

    if (
      loc.indexOf("/admin") !== -1 ||
      loc.indexOf("/budget-monitoring") !== -1 ||
      loc.indexOf("admin/preview") !== -1 ||
      loc.indexOf("new-campaign")
    ) {
      logger.debug("handleVisibilityChange", "Dont refresh", e);
      return;
    }

    logger.debug("handleVisibilityChange", "Reload userData", {
      isHidden: document[hidden]
    });

    if (!document[hidden]) {
      logger.debug("handleVisibilityChange", "Now calling loadUserProfile");
      Actions.loadUserProfile();
    }
  }

  // Warn if the browser doesnt support addEventListener or the Page Visibility API
  if (typeof document.addEventListener === "undefined" || typeof document[hidden] === "undefined") {
    logger.log(
      "addVisibilityHandler",
      "This demo requires a browser, such as Google Chrome or Firefox, that supports the Page Visibility API."
    );
  } else {
    // Handle page visibility change
    document.addEventListener(visibilityChange, handleVisibilityChange, false);
  }
}

//------------------------------------------------------------------------------------
// NOTE:
// This handler add extra logic, that helps us to log out users with malicous intent.
// With this handler we can change their status to "disapproved" in the database and
// next time their focus the open tab, we log them out.
//
//------------------------------------------------------------------------------------
addVisibilityHandler();

Actions.userLoggedIn.failed.listen(function (err) {
  logger.log("error:router:userLoggedIn:failed", "Login user failed", err);
  userIdLoggedIn = false;
  renderDOM();
});

Actions.loadUserProfile.failed.listen((err, user) => {
  logger.log("error:router:loadUserProfile:failed", "Found user failed", {
    err,
    userLoadProfileRetryCounter,
    user
  });

  if (user) {
    const { finishedOnboarding, orgas } = user;

    // Check if the user has not finished onboarding or orgas are null
    const isOnboardingIncomplete =
      finishedOnboarding === false || !orgas || Object.keys(orgas).length === 0;

    if (isOnboardingIncomplete) {
      logger.log("error:router:loadUserProfile:failed", "User didn't finish onboarding", { user });

      // Check if the current page is a signup or invite page
      const isSignUpOrInvitePage =
        window.location.href.includes("/signup-organization") ||
        window.location.href.includes("/invite");

      if (isSignUpOrInvitePage) {
        return;
      }

      // Handle user who dropped the onboarding process
      auth
        .logout()
        .then(() => {
          window.location.href = "/login";
        })
        .catch((err) => {
          logger.log("error:router:loadUserProfile:failed", "Logout failed", err);
        });
    }
    return;
  }

  if (userLoadProfileRetryCounter <= 2) {
    userLoadProfileRetryCounter += 1;
    logger.log("error:router:loadUserProfile:failed", "retrying loading user", {
      userLoadProfileRetryCounter
    });
    setTimeout(() => {
      Actions.loadUserProfile();
    }, 1000);
  }
  userIdLoggedIn = false;
  renderDOM();
});

Actions.loadUserProfile.completed.listen((userProfile) => {
  logger.log("router:loadUserProfile:completed", "Found user", userProfile.userId);

  Actions.loadOrganization(userProfile.organizationId);
  Actions.getAtsStatsByOrga(userProfile.organizationId);

  const userLang = userProfile.language || "de";

  // overwrite i18nextMiddleware.LanguageDetector
  window.language = userLang;

  i18n.changeLanguage(userLang);
  loadGoogleMaps(userLang);

  userLoadProfileRetryCounter = 0;
  renderDOM(userProfile);
});

Actions.userLoggedIn.completed.listen(async function (currentUser) {
  // If the loggedin user is anonymous then logout the user and prompt them to log back in
  if (currentUser.isAnonymous) {
    logger.log("router:userLoggedIn:completed", "User is anonymous, logging out...");
    Actions.logout();
  }

  // If the user was created before 05.23.2022 then we don't need to ask for verification
  const userSignupDate = currentUser?.metadata?.creationTime;
  const userCreatedBeforeValidation = userSignupDate
    ? moment(userSignupDate) < moment(VALIDATION_START_DATE)
    : true;

  const authPages = [
    "/invite",
    "/signup",
    "/login",
    "/signup-organization",
    "/verification-code",
    "/invite-team",
    "/thanks",
    "/onboarding"
  ];
  const isAuthPage = authPages.some((endpoint) => window.location.href.includes(endpoint));

  // If user has not verified their email yet then redirect them to the email verification view
  const isValid =
    !userCreatedBeforeValidation &&
    !currentUser?.emailVerified &&
    !isAuthPage &&
    window.location.href !== "/" &&
    !(window.emulatorConfig && window.emulatorConfig.auth && window.emulatorConfig.firestore);

  if (isValid) {
    logger.log("router:userLoggedIn:completed", "user is not verified", {
      currentUser,
      route: window.location.href,
      emulatorConfig: window.emulatorConfig
    });

    // In case the user is not valid, we send the verification code
    Actions.generateVerificationCode({
      loadUserProfile: true,
      orgaId: null
    });

    // This set timeout is important. It gives enough time for the verification code to be generated
    // Otherwise firebase has a connection error, because the whole window is reloaded
    // We have tested that 300 ms is enough time for the verification code to be generated
    // And that the UI has a good user experience
    setTimeout(() => {
      window.location.href = "/verification-code";
    }, 300);

    return;
  }

  // Adds user context to the session data
  if (window.location.href.indexOf("localhost") === -1) {
    datadogRum.setUser({
      id: currentUser.uid,
      name: currentUser.displayName,
      email: currentUser.email
    });
  }

  // This is used to prevent unnecessary renders but still check if the user permission changed
  // after resuming to the tab in the browser.
  if (userIdLoggedIn) {
    logger.log("warn:router:userLoggedIn:completed", "Dont rerender the DOM");
    return;
  }

  userIdLoggedIn = true;

  const urlPath = window.location.href.toLowerCase();
  let isUserAndNotOnSignUp =
    currentUser &&
    currentUser.uid &&
    !urlPath.includes("signup") &&
    !urlPath.includes("invite") &&
    !urlPath.includes("registrieren") &&
    !urlPath.includes("verification-code") &&
    !urlPath.includes("onboarding");

  if (isUserAndNotOnSignUp) {
    logger.log("router:userLoggedIn:completed", "Now calling loadUserProfile", {
      currentUser: currentUser?.uid
    });
    Actions.loadUserProfile();
  } else {
    logger.log("error:router:userLoggedIn:completed", "Didn't find a logged in user", {
      currentUser: currentUser?.uid,
      isUserAndNotOnSignUp,
      location: urlPath
    });
    renderDOM();
  }
});

window.onerror = function (message, source, lineno, colno, error) {
  logger.log("error:window:onerror", "Global error", {
    message,
    source,
    lineno,
    colno,
    error
  });
};

// Enable HMR (Hot Module Replacement) of Webpack
if (module.hot) module.hot.accept();
